import { createReducer } from '@reduxjs/toolkit';
import {
	getDownloadMsiInstallerUrl,
	getDownloadMsiInstallerUrlResolved,
	getDownloadMsiInstallerUrlRejected,
	deployGreengrassV2,
	deployGreengrassV2Rejected,
	deployGreengrassV2Resolved,
	getConfigV2Zip,
	getConfigV2ZipRejected,
	getConfigV2ZipResolved,
	checkStatusMsiFile,
	checkStatusMsiFileResolved,
	checkStatusMsiFileRejected,
	generateMsiFile,
	generateMsiFileResolved,
	generateMsiFileRejected,
	blockMsiFileRegenerate,
	genConfCodeResolved,
	genConfCodeRejected,
	genConfCode,
	getCertStatusResolved,
	getCertStatusRejected,
	setCertStatusResolved,
	setCertStatusRejected,
	setCertStatus,
	genConfCodeDynamicResolved,
	genConfCodeDynamicRejected,
	genConfCodeDynamic,
} from '@Actions';

import { ImplementationStoreState } from '@Types/implementation';

export const initialState: ImplementationStoreState = {
	msiInstaller: {
		status: 'idle',
		downloadUrl: '',
		buildStatus: {},
		checkingBuildStatus: false,
	},
	configV2Zip: {
		initialized: false,
		blob: undefined,
	},
	deployedGreengrassV2: {
		status: 'idle',
	},
	configCode: {
		status: 'idle',
		request: null,
		response: null,
		firstTime: false,
	},
	certificateStatus: {
		status: 'idle',
		response: null,
	},
};

const reducer = createReducer(initialState, builder =>
	builder
		.addCase(getDownloadMsiInstallerUrl, state => {
			state.msiInstaller.status = 'pending';
			state.msiInstaller.downloadUrl = '';
		})
		.addCase(getDownloadMsiInstallerUrlResolved, (state, action) => {
			const { downloadUrl }: { downloadUrl: string } = action.payload;
			state.msiInstaller.status = 'success';
			state.msiInstaller.downloadUrl = downloadUrl;
		})
		.addCase(getDownloadMsiInstallerUrlRejected, state => {
			state.msiInstaller.status = 'error';
			state.msiInstaller.downloadUrl = '';
		})
		.addCase(generateMsiFile, state => {
			state.msiInstaller.status = 'pending';
			state.msiInstaller.downloadUrl = '';
			state.msiInstaller.checkingBuildStatus = true;
			state.msiInstaller.buildStatus = {
				buildComplete: false,
				buildStatus: 'IN_PROGRESS',
				currentPhase: 'SUBMITTED',
			};
		})
		.addCase(generateMsiFileResolved, state => {
			state.msiInstaller.status = 'idle';
		})
		.addCase(generateMsiFileRejected, state => {
			state.msiInstaller.status = 'error';
		})
		.addCase(checkStatusMsiFile, state => {
			state.msiInstaller.checkingBuildStatus = true;
		})
		.addCase(blockMsiFileRegenerate, state => {
			state.msiInstaller.buildStatus.buildComplete = false;
			state.msiInstaller.buildStatus.buildStatus = 'IN_PROGRESS';
			state.msiInstaller.buildStatus.id = 'dummyId';
		})
		.addCase(checkStatusMsiFileResolved, (state, action) => {
			state.msiInstaller.buildStatus = action.payload.buildStatus;
			state.msiInstaller.checkingBuildStatus = false;
		})
		.addCase(checkStatusMsiFileRejected, state => {
			state.msiInstaller.checkingBuildStatus = false;
		})
		.addCase(deployGreengrassV2, state => {
			state.deployedGreengrassV2 = { status: 'pending' };
		})
		.addCase(deployGreengrassV2Resolved, state => {
			state.deployedGreengrassV2 = { status: 'success' };
		})
		.addCase(deployGreengrassV2Rejected, state => {
			state.deployedGreengrassV2 = { status: 'error' };
		})
		.addCase(getConfigV2Zip, state => {
			state.configV2Zip = { initialized: false };
		})
		.addCase(getConfigV2ZipResolved, (state, action) => {
			state.configV2Zip = {
				initialized: true,
				blob: action.payload as Blob,
			};
		})
		.addCase(getConfigV2ZipRejected, state => {
			state.configV2Zip = { initialized: true };
		})
		// * -- Gen code (static/ssm) -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- --
		.addCase(genConfCode, (state, action) => {
			state.configCode.status = 'pending';
			state.configCode.response = null;
			state.configCode.firstTime = action.payload.firstTime;
		})
		.addCase(genConfCodeResolved, (state, action) => {
			state.configCode.status = 'success';
			state.configCode.response = {
				...action.payload,
				expiresAt: action.payload.createdAt,
			};
		})
		.addCase(genConfCodeRejected, state => {
			state.configCode.status = 'error';
			state.configCode.response = null;
		})
		// * -- Gen code (dynamic) -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- --
		.addCase(genConfCodeDynamic, (state, action) => {
			state.configCode.status = 'pending';
			state.configCode.response = null;
			state.configCode.firstTime = action.payload.firstTime;
		})
		.addCase(genConfCodeDynamicResolved, (state, action) => {
			state.configCode.status = 'success';
			state.configCode.response = {
				...action.payload,
				expiresAt: action.payload.createdAt,
			};
		})
		.addCase(genConfCodeDynamicRejected, state => {
			state.configCode.status = 'error';
			state.configCode.response = null;
		})
		// * -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- -- --
		.addCase(getCertStatusResolved, (state, action) => {
			state.certificateStatus.status = 'success';
			state.certificateStatus.response = action.payload;
		})
		.addCase(getCertStatusRejected, state => {
			state.certificateStatus.status = 'error';
			state.certificateStatus.response = null;
		})
		.addCase(setCertStatus, state => {
			state.certificateStatus.status = 'pending';
			state.certificateStatus.response = null;
		})
		.addCase(setCertStatusResolved, (state, action) => {
			state.certificateStatus.status = 'success';
			state.certificateStatus.response = action.payload;
		})
		.addCase(setCertStatusRejected, state => {
			state.certificateStatus.status = 'error';
			state.certificateStatus.response = null;
		}),
);

export default reducer;
